/**
 * WP29 signal drawer — right-side panel that lists candidate regulatory
 * change signals lifted from the latest ingested bundle, and their linkage
 * to canonical rule IDs.
 *
 * W7-slice-b1 scope (Lesson §16, ADR-0004):
 *   - Trigger button shows pending-signal count next to a bell glyph.
 *   - Drawer (dialog role) lists each signal with: instrument, article,
 *     change-kind chip, lifecycleState chip (candidate / promoted / dismissed),
 *     effective date, summary, affected rule chips.
 *   - WP29 intake (audit G8): PRIMARY loads the deployed feeder's real archived
 *     feed (→ /v1/wp29/ingest); secondary live-feeder trigger; small dev-only
 *     mock loader (→ /v1/wp29/mock-ingest).
 *   - Rule chips are buttons → click bubbles up the rule id so the parent
 *     can open the Requirement Inspector on that card.
 *   - "Promote → canonical" and "Dismiss" buttons NOW ENABLED — call
 *     POST /v1/signals/:id/(promote|dismiss). Single write path enforced
 *     by Lesson §16: signal lifecycleState changes ONLY through this route.
 *     Already-finalised signals show their decision instead of the buttons.
 */

import { useEffect, useRef, useState } from "react";
import type { PromotedRule, SignalLifecycleState, Wp29Signal } from "@regpulse/contracts";
import { TOPIC_REGISTRY } from "@regpulse/contracts";
import type { LinkedSignal, SignalBundleMeta } from "../services/api";
import { RuleAuthoringForm } from "./RuleAuthoringForm";

const CHANGE_KIND_LABEL: Record<Wp29Signal["changeKind"], string> = {
  amendment: "Amendment",
  implementing_act: "Implementing act",
  new_regulation: "New regulation",
  guidance_update: "Guidance",
  withdrawal: "Withdrawal",
  delegated_act: "Delegated act",
  implementing_decision: "Implementing decision",
  source_amendment: "Source amendment",
  draft: "Draft",
  proposed: "Proposed",
  market_voice: "Market voice",
};

const LIFECYCLE_CHIP: Record<SignalLifecycleState, { label: string; bg: string; fg: string }> = {
  candidate: {
    label: "Candidate",
    bg: "var(--ft-evidence-soft)",
    fg: "var(--ft-evidence)",
  },
  under_review: {
    label: "Under review",
    bg: "var(--ft-market-force-soft)",
    fg: "var(--ft-market-force)",
  },
  promoted: {
    label: "Promoted",
    bg: "var(--ft-hard-law-soft)",
    fg: "var(--ft-hard-law)",
  },
  dismissed: {
    label: "Dismissed",
    bg: "var(--color-surface-sunken)",
    fg: "var(--color-text-disabled)",
  },
};

interface Props {
  open: boolean;
  onClose: () => void;
  signals: Wp29Signal[];
  bundle: SignalBundleMeta | null;
  linkedSignals: LinkedSignal[];
  onIngest: () => void;
  ingesting: boolean;
  /** Audit G5 — optional LIVE WP29 feeder trigger (the deployed wp29-feeder
   *  worker). When absent, only the mock path is shown. */
  onTriggerLive?: () => void;
  liveFeederBusy?: boolean;
  liveFeederStatus?: { kind: "ok" | "error"; message: string } | null;
  /** Audit G8 — optional loader for the latest REAL WP29 weekly feed snapshot
   *  (the deployed feeder's actual archived output) so the 草案 axis shows real
   *  data during local review without a deployed core to receive the live push. */
  onLoadLatestFeed?: () => void;
  latestFeedBusy?: boolean;
  latestFeedWeek?: string;
  onSelectRule: (ruleId: string) => void;
  /**
   * Apply a review decision. Returns once the API call settles and
   * lifts the responsibility of refetching signals back to the parent.
   * `opts.persistTier` (B3) only applies to "promote": "canonical" = the
   * promote record; "raglight" = also index the source as advisory context.
   */
  onDecide: (
    signalId: string,
    decision: "promote" | "dismiss" | "revert",
    opts?: { persistTier?: "canonical" | "raglight" },
  ) => Promise<void>;
  /**
   * D3 (ADR-0010 Decision 8): accept a candidate AS canonical by authoring + signing a
   * `PromotedRule`. Distinct from `onDecide("promote", {raglight})` (advisory index) — this is
   * the only path that materialises a board card. The drawer opens the authoring form; the
   * parent POSTs the signed rule and re-evaluates.
   */
  onPromoteCanonical: (signalId: string, rule: PromotedRule) => Promise<void>;
  /**
   * D4 (ADR-0008 / RFC-0003 §4): accept a candidate into U "Case intelligence" — a third ADVISORY
   * destination. Never canonical, never the board; the signal lands unverified (`reviewedBy: null`).
   */
  onAcceptUseCase: (signalId: string) => Promise<void>;
  /** RFC-0007 — paste a TRIS (2015/1535) notification email → country-tagged
   *  ADVISORY national-draft candidate (same review queue). Optional: hidden
   *  when the parent doesn't wire it. */
  onTrisIngest?: (emailText: string) => Promise<void>;
  trisIngesting?: boolean;
  /** Run a manual discovery query (RFC-0002 C2): exa+RAGLight+LLM → candidates. */
  onDiscover: (query: string) => Promise<void>;
  /** Run config-derived discovery (parent derives queries from the evaluation). */
  onDiscoverForConfig: () => Promise<void>;
  discovering: boolean;
  /** Last discovery run's source mode (live/fixture) for the badge; null = none yet. */
  discoveryMode: { exa: string; raglight: string; llm: string } | null;
  /** Whether an evaluation exists so config-derived discovery is meaningful. */
  configQueryAvailable: boolean;
  /** RFC-0005 VOC live collect → market_voice candidates (same review queue).
   *  RFC-0008 §2.7: `topicSweep` runs the config×topic sweep instead of a keyword. */
  onVocIngest: (params: {
    brand: string;
    keyword?: string;
    topicSweep?: boolean;
    topicIds?: string[];
  }) => Promise<void>;
  vocIngesting: boolean;
  /** Channel status from the last VOC ingest (openai / exa / mcp). */
  vocChannels: Record<"openai" | "exa" | "mcp", string> | null;
  vocBrandDefault?: string;
  vocKeywordDefault?: string;
  /** Reassign a VOC signal's topic attribution (replaces its market_voice
   *  spine edges) — fixes vocabulary-gap / sticky-selection misattribution
   *  without dismiss-and-recollect. Optional: hidden when not wired. */
  onReassignTopics?: (signalId: string, topicIds: string[]) => Promise<void>;
}

type SignalFilter = "all" | "discovered" | "wp29" | "voc" | "needs-review";

const FILTERS: { key: SignalFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "discovered", label: "🆕 Discovered" },
  { key: "wp29", label: "WP29" },
  { key: "voc", label: "VOC" },
  { key: "needs-review", label: "Needs review" },
];

/** A signal is "discovered" (vs WP29-ingested) when it came from the discovery
 *  pipeline, which stamps a `disc-` bundle id (RFC-0002 / ADR-0009). */
function isDiscovered(s: Wp29Signal): boolean {
  return s.bundleId.startsWith("disc-");
}

function isVocSignal(s: Wp29Signal): boolean {
  return s.changeKind === "market_voice" || s.id.startsWith("voc-");
}

function signalSourceKey(s: Wp29Signal): "discovered" | "voc" | "wp29" {
  if (isDiscovered(s)) return "discovered";
  if (isVocSignal(s)) return "voc";
  return "wp29";
}

const SIGNAL_SOURCE_LABEL: Record<ReturnType<typeof signalSourceKey>, string> = {
  discovered: "🆕 Discovered",
  voc: "VOC",
  wp29: "WP29",
};

function sourceHost(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return "source";
  }
}

export function SignalDrawer({
  open,
  onClose,
  signals,
  bundle,
  linkedSignals,
  onIngest,
  ingesting,
  onTriggerLive,
  liveFeederBusy,
  liveFeederStatus,
  onLoadLatestFeed,
  latestFeedBusy,
  latestFeedWeek,
  onSelectRule,
  onDecide,
  onPromoteCanonical,
  onAcceptUseCase,
  onDiscover,
  onDiscoverForConfig,
  discovering,
  discoveryMode,
  configQueryAvailable,
  onVocIngest,
  vocIngesting,
  vocChannels,
  onTrisIngest,
  trisIngesting,
  onReassignTopics,
  vocBrandDefault = "Chery",
  vocKeywordDefault = "lane keeping",
}: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [vocBrand, setVocBrand] = useState(vocBrandDefault);
  const [vocKeyword, setVocKeyword] = useState(vocKeywordDefault);
  const [trisEmailText, setTrisEmailText] = useState("");
  // Optional human-chosen topics for the collected VOC (multi-select; avoids the
  // "unclassified" bucket and lets one complaint span several topics).
  const [vocTopics, setVocTopics] = useState<string[]>([]);
  const [vocTopicFilter, setVocTopicFilter] = useState("");
  // Per-signal in-flight flag so the right button can show "deciding…"
  // without disabling the whole drawer.
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [decideError, setDecideError] = useState<string | null>(null);
  // D3: which candidate's canonical-authoring form is open (one at a time).
  const [authoringSignalId, setAuthoringSignalId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<SignalFilter>("all");

  // Reviewer identity for the authored rule. The signed PromotedRule requires a valid email
  // (ADR-0010 Decision 8 trust bar), so the fallback is a valid-email placeholder the reviewer
  // edits in the form — not the bare "reviewer@local" used for non-signing decisions.
  const reviewer =
    (typeof window !== "undefined" && window.localStorage?.getItem("regpulse_reviewer")) ||
    "reviewer@regpulse.local";

  const handleAuthorSubmit = async (sid: string, rule: PromotedRule) => {
    setDecidingId(sid);
    setDecideError(null);
    try {
      await onPromoteCanonical(sid, rule);
      setAuthoringSignalId(null);
    } catch (err) {
      setDecideError(`promote ${sid} failed: ${(err as Error).message ?? "unknown error"}`);
    } finally {
      setDecidingId(null);
    }
  };

  const handleAcceptUseCase = async (sid: string) => {
    setDecidingId(sid);
    setDecideError(null);
    try {
      await onAcceptUseCase(sid);
    } catch (err) {
      setDecideError(`accept-to-U ${sid} failed: ${(err as Error).message ?? "unknown error"}`);
    } finally {
      setDecidingId(null);
    }
  };

  const handleDecide = async (
    sid: string,
    decision: "promote" | "dismiss" | "revert",
    persistTier?: "canonical" | "raglight",
  ) => {
    setDecidingId(sid);
    setDecideError(null);
    try {
      await onDecide(sid, decision, persistTier ? { persistTier } : undefined);
    } catch (err) {
      setDecideError(
        `${decision} ${sid} failed: ${(err as Error).message ?? "unknown error"}`,
      );
    } finally {
      setDecidingId(null);
    }
  };

  const handleDiscover = async () => {
    const q = query.trim();
    if (!q) return;
    setDecideError(null);
    try {
      await onDiscover(q);
    } catch (err) {
      setDecideError(`discovery failed: ${(err as Error).message ?? "unknown error"}`);
    }
  };

  // Wrap config-derived discovery in the same error channel as manual search,
  // so a rejected runDiscovery surfaces in the UI instead of going unhandled.
  const handleDiscoverForConfig = async () => {
    setDecideError(null);
    try {
      await onDiscoverForConfig();
    } catch (err) {
      setDecideError(`discovery failed: ${(err as Error).message ?? "unknown error"}`);
    }
  };

  // Reassign-topics panel state (one signal at a time).
  const [reassigningSignalId, setReassigningSignalId] = useState<string | null>(null);
  const [reassignTopicIds, setReassignTopicIds] = useState<string[]>([]);
  const [reassignFilter, setReassignFilter] = useState("");
  const [reassignBusy, setReassignBusy] = useState(false);

  const handleReassignSave = async (signalId: string) => {
    if (!onReassignTopics || reassignTopicIds.length === 0) return;
    setReassignBusy(true);
    setDecideError(null);
    try {
      await onReassignTopics(signalId, reassignTopicIds);
      setReassigningSignalId(null);
      setReassignTopicIds([]);
      setReassignFilter("");
    } catch (err) {
      setDecideError(`重新归类失败: ${(err as Error).message ?? "unknown error"}`);
    } finally {
      setReassignBusy(false);
    }
  };

  const handleVocIngest = async () => {
    const brand = vocBrand.trim();
    const keyword = vocKeyword.trim();
    if (!brand || !keyword) return;
    setDecideError(null);
    try {
      await onVocIngest({ brand, keyword, topicIds: vocTopics.length ? vocTopics : undefined });
      // Success: clear the topic multi-select. A sticky selection left over from
      // one collect silently re-attributed the NEXT collect's signals (the
      // speed-limiter→充电体验 misattribution) — selections apply to exactly one
      // collect, never linger.
      setVocTopics([]);
    } catch (err) {
      setDecideError(`VOC ingest failed: ${(err as Error).message ?? "unknown error"}`);
    }
  };

  const handleVocSweep = () => {
    const brand = vocBrand.trim();
    if (!brand) return;
    setDecideError(null);
    // App's handler rethrows after surfacing its own error banner — catch here
    // so the sweep button never produces an unhandled rejection.
    void onVocIngest({ brand, topicSweep: true }).catch((err: unknown) => {
      setDecideError(`VOC sweep failed: ${(err as Error).message ?? "unknown error"}`);
    });
  };

  useEffect(() => {
    if (!open) return;
    setVocBrand(vocBrandDefault);
    setVocKeyword(vocKeywordDefault);
  }, [open, vocBrandDefault, vocKeywordDefault]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    dialogRef.current?.focus();
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  // Build a reverse lookup: signalId → ruleIds it affects.
  const ruleIdsBySignal = new Map<string, string[]>();
  for (const link of linkedSignals) {
    for (const sid of link.signalIds) {
      const list = ruleIdsBySignal.get(sid) ?? [];
      list.push(link.ruleId);
      ruleIdsBySignal.set(sid, list);
    }
  }

  // Filter + sort: candidate (needs review) first → discovered before WP29 →
  // recency (candidateOpenedAt desc). Pure view transform; never mutates lanes.
  const visibleSignals = signals
    .filter((s) => {
      if (filter === "discovered") return isDiscovered(s);
      if (filter === "wp29") return !isDiscovered(s) && !isVocSignal(s);
      if (filter === "voc") return isVocSignal(s);
      if (filter === "needs-review") return s.lifecycleState === "candidate";
      return true;
    })
    .slice()
    .sort((a, b) => {
      const ac = a.lifecycleState === "candidate" ? 0 : 1;
      const bc = b.lifecycleState === "candidate" ? 0 : 1;
      if (ac !== bc) return ac - bc;
      const ad = isDiscovered(a) ? 0 : 1;
      const bd = isDiscovered(b) ? 0 : 1;
      if (ad !== bd) return ad - bd;
      return (b.candidateOpenedAt ?? "").localeCompare(a.candidateOpenedAt ?? "");
    });

  return (
    <div
      role="presentation"
      data-testid="signal-drawer-backdrop"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.32)",
        zIndex: 100,
      }}
    >
      <aside
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="signal-drawer-title"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        data-testid="signal-drawer"
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(540px, 96vw)",
          background: "var(--color-surface)",
          borderLeft: "1px solid var(--color-border)",
          boxShadow: "-8px 0 24px rgba(0,0,0,0.12)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <header
          style={{
            padding: "var(--space-12)",
            borderBottom: "1px solid var(--color-border)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "var(--space-8)",
          }}
        >
          <div>
            <h2 id="signal-drawer-title" className="type-h3" style={{ margin: 0, fontSize: 14 }}>
              <span aria-hidden style={{ marginRight: 6 }}>
                🛎
              </span>
              Discovery &amp; Review
            </h2>
            <p
              className="type-caption"
              style={{ margin: "var(--space-4) 0 0 0", color: "var(--color-text-secondary)" }}
            >
              Discover regulatory changes (advisory) and review candidates. Human review is the
              only path to canonical; provenance triplet preserved (CI red line #4).
            </p>
          </div>
          <button
            data-testid="signal-drawer-close"
            onClick={onClose}
            aria-label="Close signal drawer"
            style={{
              border: "1px solid var(--color-border-strong)",
              background: "var(--color-surface-sunken)",
              borderRadius: "var(--radius-4)",
              padding: "var(--space-4) var(--space-8)",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            ✕
          </button>
        </header>

        {/* ── Discovery trigger (RFC-0002 C2): manual query + config-derived ── */}
        <section
          data-testid="discovery-trigger"
          style={{
            padding: "var(--space-12)",
            borderBottom: "1px solid var(--color-border)",
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-8)",
          }}
        >
          <div style={{ display: "flex", gap: "var(--space-8)", alignItems: "center" }}>
            <input
              aria-label="Discovery query"
              data-testid="discovery-query-input"
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
              onKeyDown={(e) => {
                // Guard like the Search button so rapid Enter presses can't fan out overlapping
                // discovery requests while one is in flight.
                if (e.key === "Enter" && !discovering && query.trim().length > 0) void handleDiscover();
              }}
              placeholder="Search regulatory changes (e.g. UN R155)…"
              style={{
                flex: 1,
                fontSize: 12,
                padding: "var(--space-4) var(--space-8)",
                border: "1px solid var(--color-border-strong)",
                borderRadius: "var(--radius-4)",
                background: "var(--color-surface)",
                color: "var(--color-text-primary)",
              }}
            />
            <button
              data-testid="discovery-search"
              onClick={() => void handleDiscover()}
              disabled={discovering || query.trim().length === 0}
              style={{
                fontSize: 12,
                padding: "var(--space-4) var(--space-12)",
                border: "1px solid var(--color-accent)",
                background: "var(--color-accent-soft)",
                color: "var(--color-accent)",
                borderRadius: "var(--radius-4)",
                cursor: discovering ? "wait" : "pointer",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              {discovering ? "Searching…" : "Search"}
            </button>
          </div>
          <div style={{ display: "flex", gap: "var(--space-8)", alignItems: "center", flexWrap: "wrap" }}>
            <button
              data-testid="discovery-for-config"
              onClick={() => void handleDiscoverForConfig()}
              disabled={discovering || !configQueryAvailable}
              title={
                configQueryAvailable
                  ? "Derive discovery queries from the loaded vehicle config"
                  : "Run applicability first to enable config-derived discovery"
              }
              style={{
                fontSize: 11,
                padding: "var(--space-4) var(--space-8)",
                border: "1px solid var(--color-border-strong)",
                background: "var(--color-surface)",
                // Explicit colour — without it the label inherited a dark tone and was invisible
                // on the dark surface (browser-verification finding).
                color: "var(--color-text-primary)",
                borderRadius: "var(--radius-4)",
                cursor: discovering || !configQueryAvailable ? "not-allowed" : "pointer",
                opacity: configQueryAvailable ? 1 : 0.5,
              }}
            >
              ⟳ Discover updates for current config
            </button>
            {discoveryMode && (
              <span
                data-testid="discovery-mode"
                className="type-mono"
                style={{
                  fontSize: 10,
                  padding: "1px 6px",
                  borderRadius: "var(--radius-4)",
                  border: "1px solid var(--color-border)",
                  color: "var(--color-text-secondary)",
                }}
                title="Whether the last discovery ran live or on fixtures"
              >
                exa: {discoveryMode.exa} · LLM: {discoveryMode.llm}
              </span>
            )}
          </div>
          <p className="type-caption" style={{ margin: 0, color: "var(--color-text-disabled)", fontSize: 10 }}>
            Discovered items are advisory leads — not canonical, require human review.
          </p>
        </section>

        <section
          style={{
            padding: "var(--space-12)",
            borderBottom: "1px solid var(--color-border)",
            background: "var(--color-surface-sunken)",
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-8)",
          }}
        >
          <h3 className="type-caption" style={{ margin: 0, fontSize: 11, fontWeight: 600 }}>
            WP29 法规草案 · feed 摄取
          </h3>
          {bundle && (
            <div style={{ fontSize: 11 }}>
              <div style={{ fontWeight: 700, fontFamily: "var(--font-mono)" }}>{bundle.bundleId}</div>
              <div style={{ color: "var(--color-text-secondary)" }}>
                fetched {bundle.fetchedAt} · {bundle.count} signal(s)
              </div>
            </div>
          )}

          {/* PRIMARY — load the latest REAL feeder feed (advisory, never canonical). */}
          {onLoadLatestFeed && (
            <button
              type="button"
              data-testid="signal-load-latest-feed"
              onClick={onLoadLatestFeed}
              disabled={latestFeedBusy}
              title="载入最新真实 WP29 周抓取（deployed feeder 已归档到 R2 的真实产物）到 core，点亮三轴分析的「草案」轴"
              style={{
                fontSize: 12,
                padding: "var(--space-8) var(--space-12)",
                border: "1px solid var(--color-accent)",
                background: "var(--color-accent-soft)",
                color: "var(--color-accent)",
                borderRadius: "var(--radius-4)",
                cursor: latestFeedBusy ? "wait" : "pointer",
                fontWeight: 600,
              }}
            >
              {latestFeedBusy
                ? "载入真实 feed…"
                : `↻ 载入最新真实 WP29 feed${latestFeedWeek ? ` (${latestFeedWeek})` : ""}`}
            </button>
          )}

          {/* SECONDARY — live real-time pull, plus a small dev-only mock loader. */}
          <div style={{ display: "flex", gap: "var(--space-8)", flexWrap: "wrap", alignItems: "center" }}>
            {onTriggerLive && (
              <button
                type="button"
                data-testid="signal-trigger-live"
                onClick={onTriggerLive}
                disabled={liveFeederBusy}
                title="触发部署的 wp29-feeder 实时周拉取（需配置 VITE_WP29_FEEDER_URL + 可达 core）"
                style={{
                  fontSize: 11,
                  padding: "var(--space-4) var(--space-8)",
                  border: "1px solid var(--color-border-strong)",
                  background: "var(--color-surface)",
                  color: "var(--color-text-primary)",
                  borderRadius: "var(--radius-4)",
                  cursor: liveFeederBusy ? "wait" : "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {liveFeederBusy ? "拉取实时数据…" : "触发实时拉取 (live feeder)"}
              </button>
            )}
            <button
              type="button"
              data-testid="signal-ingest-mock"
              onClick={onIngest}
              disabled={ingesting}
              title="dev 测试用：载入 W19 mock bundle（已生效法案夹具，不挂 topic、不点亮草案轴）"
              style={{
                fontSize: 11,
                padding: "var(--space-4) var(--space-8)",
                border: "1px dashed var(--color-border)",
                background: "var(--color-surface)",
                color: "var(--color-text-secondary)",
                borderRadius: "var(--radius-4)",
                cursor: ingesting ? "wait" : "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {ingesting ? "载入 mock…" : "载入 mock bundle (dev 测试)"}
            </button>
          </div>
          {liveFeederStatus && (
            <span
              data-testid="wp29-live-status"
              role={liveFeederStatus.kind === "error" ? "alert" : "status"}
              style={{
                fontSize: 10,
                color:
                  liveFeederStatus.kind === "error"
                    ? "var(--status-candidate-blocked)"
                    : "var(--color-text-secondary)",
              }}
            >
              {liveFeederStatus.message}
            </span>
          )}

          {/* TRIS national drafts (RFC-0007): paste a 2015/1535 notification email. */}
          {onTrisIngest && (
            <details data-testid="tris-ingest" style={{ marginTop: "var(--space-8)" }}>
              <summary
                className="type-caption"
                style={{ cursor: "pointer", fontSize: 11, fontWeight: 600 }}
              >
                TRIS 国家草案 · 粘贴通知邮件（2015/1535）
              </summary>
              <p className="type-caption" style={{ margin: "var(--space-4) 0", fontSize: 10, color: "var(--color-text-disabled)" }}>
                把 TRIS 订阅邮箱收到的通知邮件正文粘贴到这里——解析为带国家码的 advisory
                国家草案候选（同一人审队列，绝不自动促成）。
              </p>
              <textarea
                aria-label="TRIS notification email"
                data-testid="tris-email-input"
                value={trisEmailText}
                onChange={(e) => setTrisEmailText(e.currentTarget.value)}
                placeholder={"Notification Number: 2026/0123/FR\nTitle: …\nCategory: Electricity\nEnd of standstill: …"}
                rows={5}
                style={{
                  width: "100%",
                  fontSize: 11,
                  fontFamily: "var(--font-mono)",
                  padding: "var(--space-8)",
                  border: "1px solid var(--color-border-strong)",
                  borderRadius: "var(--radius-4)",
                  background: "var(--color-surface)",
                  color: "var(--color-text-primary)",
                  resize: "vertical",
                }}
              />
              <button
                type="button"
                data-testid="tris-ingest-submit"
                disabled={trisIngesting || !trisEmailText.trim()}
                onClick={() => {
                  const text = trisEmailText;
                  // Clear ONLY on success AND only if unchanged — text typed
                  // during the in-flight ingest must survive; on failure the
                  // parent surfaces the error and the paste stays for a retry.
                  void onTrisIngest(text)
                    .then(() => setTrisEmailText((cur) => (cur === text ? "" : cur)))
                    .catch(() => {});
                }}
                style={{
                  marginTop: "var(--space-4)",
                  fontSize: 11,
                  padding: "var(--space-4) var(--space-8)",
                  border: "1px solid var(--color-border-strong)",
                  background: "var(--color-surface)",
                  color: "var(--color-text-primary)",
                  borderRadius: "var(--radius-4)",
                  cursor: trisIngesting ? "wait" : "pointer",
                }}
              >
                {trisIngesting ? "解析中…" : "解析并入审查队列"}
              </button>
            </details>
          )}
        </section>

        {/* ── VOC collect (RFC-0005): OpenAI + Exa + MCP → market_voice candidates ── */}
        <section
          data-testid="voc-ingest-trigger"
          style={{
            padding: "var(--space-12)",
            borderBottom: "1px solid var(--color-border)",
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-8)",
          }}
        >
          <h3 className="type-caption" style={{ margin: 0, fontSize: 11, fontWeight: 600 }}>
            Voice of Customer (VOC) · 市场痛点
          </h3>
          <p className="type-caption" style={{ margin: 0, fontSize: 10, color: "var(--color-text-disabled)" }}>
            三通道实时搜索（OpenAI web · Exa · MCP），可能 1–2 分钟。结果为 advisory market_voice，仅供人工复核。
          </p>

          {/* Brand — shared by both paths. */}
          <input
            aria-label="VOC brand"
            data-testid="voc-brand-input"
            value={vocBrand}
            onChange={(e) => setVocBrand(e.currentTarget.value)}
            placeholder="品牌（如 Chery / Jaecoo）"
            style={{
              fontSize: 12,
              padding: "var(--space-4) var(--space-8)",
              border: "1px solid var(--color-border-strong)",
              borderRadius: "var(--radius-4)",
            }}
          />

          {/* PRIMARY (RFC-0008 §2.7): config×topic sweep — no keyword / topic pick. */}
          <button
            type="button"
            data-testid="voc-config-sweep"
            onClick={handleVocSweep}
            disabled={vocIngesting || !vocBrand.trim()}
            title="按车型配置（当前固定为内置试点配置 MY2027 BEV，同行动简报）的适用 topic 自动扫描市场痛点（每个 topic 的英文关键词 + 品牌），自动归类——无需手敲关键词或手选 topic（RFC-0008 §2.7）"
            style={{
              fontSize: 12,
              padding: "var(--space-8) var(--space-12)",
              border: "1px solid var(--color-accent)",
              background: "var(--color-accent-soft)",
              color: "var(--color-accent)",
              borderRadius: "var(--radius-4)",
              cursor: vocIngesting ? "wait" : "pointer",
              fontWeight: 600,
            }}
          >
            {vocIngesting ? "扫描中…" : "↻ 扫描本配置全部痛点（推荐）"}
          </button>
          <p className="type-caption" style={{ margin: 0, fontSize: 10, color: "var(--color-text-disabled)" }}>
            自动扫描车型配置下所有适用 topic，结果自动归类——无需手敲关键词或手选 topic。
          </p>

          {/* SECONDARY: manual free-text collect (ad-hoc / supplement) — collapsed. */}
          <details data-testid="voc-manual">
            <summary
              className="type-caption"
              style={{ cursor: "pointer", fontSize: 11, fontWeight: 600 }}
            >
              手动收集（自由关键词 · 临时补充）
            </summary>
            <div
              style={{
                display: "flex",
                gap: "var(--space-8)",
                flexWrap: "wrap",
                marginTop: "var(--space-8)",
              }}
            >
              <input
                aria-label="VOC keyword"
                data-testid="voc-keyword-input"
                value={vocKeyword}
                onChange={(e) => setVocKeyword(e.currentTarget.value)}
                placeholder="自由关键词（如 lane keeping）"
                style={{
                  flex: "2 1 180px",
                  fontSize: 12,
                  padding: "var(--space-4) var(--space-8)",
                  border: "1px solid var(--color-border-strong)",
                  borderRadius: "var(--radius-4)",
                }}
              />
              <button
                data-testid="voc-ingest-submit"
                onClick={() => void handleVocIngest()}
                disabled={vocIngesting || !vocBrand.trim() || !vocKeyword.trim()}
                style={{
                  fontSize: 12,
                  padding: "var(--space-4) var(--space-12)",
                  border: "1px solid var(--color-border-strong)",
                  background: "var(--color-surface)",
                  color: "var(--color-text-primary)",
                  borderRadius: "var(--radius-4)",
                  cursor: vocIngesting ? "wait" : "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {vocIngesting ? "收集中…" : "Collect VOC"}
              </button>
            </div>
          <div data-testid="voc-topics" style={{ marginTop: "var(--space-8)" }}>
            <div
              style={{
                fontSize: 11,
                color: "var(--color-text-secondary)",
                marginBottom: "var(--space-4)",
              }}
            >
              归类到主题（可多选；不选则按关键词自动归类）
              {vocTopics.length > 0 ? ` · 已选 ${vocTopics.length}` : ""}
            </div>
            {vocTopics.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "var(--space-4)",
                  marginBottom: "var(--space-4)",
                }}
              >
                {vocTopics.map((id) => {
                  const t = TOPIC_REGISTRY.find((x) => x.id === id);
                  return (
                    <button
                      type="button"
                      key={id}
                      data-testid={`voc-topic-chip-${id}`}
                      onClick={() => setVocTopics((prev) => prev.filter((x) => x !== id))}
                      style={{
                        fontSize: 11,
                        padding: "var(--space-4) var(--space-8)",
                        border: "1px solid var(--color-accent)",
                        background: "var(--color-accent-soft)",
                        color: "var(--color-accent)",
                        borderRadius: "var(--radius-full)",
                        cursor: "pointer",
                      }}
                    >
                      {t ? t.labelZh : id} ✕
                    </button>
                  );
                })}
              </div>
            )}
            <input
              aria-label="筛选主题"
              data-testid="voc-topic-filter"
              value={vocTopicFilter}
              onChange={(e) => setVocTopicFilter(e.currentTarget.value)}
              placeholder="筛选主题…"
              style={{
                width: "100%",
                boxSizing: "border-box",
                fontSize: 12,
                padding: "var(--space-4) var(--space-8)",
                border: "1px solid var(--color-border-strong)",
                borderRadius: "var(--radius-4)",
                marginBottom: "var(--space-4)",
              }}
            />
            <div
              style={{
                maxHeight: 140,
                overflowY: "auto",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-4)",
                padding: "var(--space-4)",
              }}
            >
              {TOPIC_REGISTRY.filter((t) => t.id !== "unclassified")
                .filter((t) => {
                  const q = vocTopicFilter.trim().toLowerCase();
                  if (!q) return true;
                  return (
                    t.id.toLowerCase().includes(q) ||
                    t.labelEn.toLowerCase().includes(q) ||
                    t.labelZh.toLowerCase().includes(q)
                  );
                })
                .map((t) => (
                  <label
                    key={t.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--space-8)",
                      fontSize: 12,
                      padding: "2px 0",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      data-testid={`voc-topic-opt-${t.id}`}
                      checked={vocTopics.includes(t.id)}
                      onChange={(e) => {
                        // Read the value BEFORE setState — the lazy updater runs
                        // after the event dispatch, when `currentTarget` is null.
                        const checked = e.currentTarget.checked;
                        setVocTopics((prev) =>
                          checked ? [...prev, t.id] : prev.filter((x) => x !== t.id),
                        );
                      }}
                    />
                    <span>
                      {t.labelZh} · {t.labelEn}
                    </span>
                  </label>
                ))}
            </div>
          </div>
          </details>
          {vocChannels && (
            <span
              data-testid="voc-channels"
              className="type-mono"
              style={{ fontSize: 10, color: "var(--color-text-secondary)" }}
            >
              openai: {vocChannels.openai} · exa: {vocChannels.exa} · mcp: {vocChannels.mcp}
            </span>
          )}
        </section>

        {signals.length > 0 && (
          <div
            data-testid="signal-filters"
            role="group"
            aria-label="Filter signals"
            style={{
              display: "flex",
              gap: "var(--space-4)",
              flexWrap: "wrap",
              padding: "var(--space-8) var(--space-12)",
              borderBottom: "1px solid var(--color-border)",
            }}
          >
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`signal-filter-${f.key}`}
                aria-pressed={filter === f.key}
                onClick={() => setFilter(f.key)}
                style={{
                  fontSize: 10,
                  padding: "1px 8px",
                  borderRadius: "var(--radius-full)",
                  border: "1px solid var(--color-border-strong)",
                  background: filter === f.key ? "var(--color-accent-soft)" : "var(--color-surface)",
                  color: filter === f.key ? "var(--color-accent)" : "var(--color-text-secondary)",
                  cursor: "pointer",
                  fontWeight: filter === f.key ? 700 : 400,
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        <ul
          aria-label="Signals"
          style={{
            listStyle: "none",
            padding: "var(--space-12)",
            margin: 0,
            overflowY: "auto",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-12)",
          }}
        >
          {decideError && (
            <li
              role="alert"
              data-testid="signal-decide-error"
              style={{
                background: "var(--status-blocker-soft)",
                color: "var(--status-blocker)",
                padding: "var(--space-8)",
                borderRadius: "var(--radius-4)",
                fontSize: 11,
                border: "1px solid var(--status-blocker)",
              }}
            >
              {decideError}
            </li>
          )}
          {signals.length === 0 && (
            <li style={{ color: "var(--color-text-secondary)", fontSize: 12 }}>
              No signals yet. Run a discovery search above, or ingest the WP29 mock bundle.
            </li>
          )}
          {signals.length > 0 && visibleSignals.length === 0 && (
            <li data-testid="signal-filter-empty" style={{ color: "var(--color-text-secondary)", fontSize: 12 }}>
              No signals match this filter.
            </li>
          )}
          {visibleSignals.map((s) => {
            const linkedRuleIds = ruleIdsBySignal.get(s.id) ?? s.affectedRuleIds;
            return (
              <li
                key={s.id}
                data-testid={`signal-item-${s.id}`}
                style={{
                  border: "1px solid var(--color-border)",
                  borderLeft: "4px solid var(--ft-market-force)",
                  borderRadius: "var(--radius-6)",
                  padding: "var(--space-8)",
                  background: "var(--color-surface)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "var(--space-8)",
                    alignItems: "baseline",
                  }}
                >
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--color-text-secondary)" }}>
                    {s.id}
                  </div>
                  <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    <span
                      data-testid={`signal-source-${s.id}`}
                      data-source={signalSourceKey(s)}
                      style={{
                        fontSize: 10,
                        padding: "1px 6px",
                        borderRadius: "var(--radius-full)",
                        background:
                          signalSourceKey(s) === "discovered"
                            ? "var(--ft-ecosystem-soft, var(--color-surface-sunken))"
                            : "var(--color-surface-sunken)",
                        color: "var(--color-text-secondary)",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {SIGNAL_SOURCE_LABEL[signalSourceKey(s)]}
                    </span>
                    <span
                      data-chip="lifecycle-state"
                      data-lifecycle={s.lifecycleState}
                      data-testid={`signal-lifecycle-${s.id}`}
                      style={{
                        fontSize: 10,
                        padding: "1px 6px",
                        borderRadius: "var(--radius-full)",
                        background: LIFECYCLE_CHIP[s.lifecycleState].bg,
                        color: LIFECYCLE_CHIP[s.lifecycleState].fg,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {LIFECYCLE_CHIP[s.lifecycleState].label}
                    </span>
                    <span
                      data-chip="change-kind"
                      data-change-kind={s.changeKind}
                      style={{
                        fontSize: 10,
                        padding: "1px 6px",
                        borderRadius: "var(--radius-full)",
                        background: "var(--ft-market-force-soft)",
                        color: "var(--ft-market-force)",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {CHANGE_KIND_LABEL[s.changeKind]}
                    </span>
                  </div>
                </div>
                <h3 className="type-h3" style={{ margin: "var(--space-4) 0", fontSize: 13 }}>
                  {s.title}
                </h3>
                <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
                  <strong>{s.instrument}</strong>
                  {s.article && <> · {s.article}</>}
                  {s.effectiveDate && <> · effective {s.effectiveDate}</>}
                </div>
                <p style={{ fontSize: 12, margin: "var(--space-8) 0", lineHeight: 1.4 }}>{s.summary}</p>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "var(--space-4)",
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>Affects:</span>
                  {linkedRuleIds.length === 0 && (
                    <span style={{ fontSize: 11, fontStyle: "italic", color: "var(--color-text-disabled)" }}>
                      no canonical match yet · curation gap
                    </span>
                  )}
                  {linkedRuleIds.map((rid) => (
                    <button
                      key={rid}
                      data-testid={`signal-rule-chip-${rid}`}
                      onClick={() => onSelectRule(rid)}
                      style={{
                        fontSize: 11,
                        padding: "1px 8px",
                        border: "1px solid var(--ft-hard-law)",
                        borderRadius: "var(--radius-full)",
                        background: "var(--ft-hard-law-soft)",
                        color: "var(--ft-hard-law)",
                        cursor: "pointer",
                        fontFamily: "var(--font-mono)",
                      }}
                    >
                      {rid}
                    </button>
                  ))}
                </div>
                <div
                  style={{
                    marginTop: "var(--space-8)",
                    display: "flex",
                    gap: "var(--space-8)",
                    fontSize: 11,
                    alignItems: "center",
                  }}
                >
                  {s.lifecycleState === "candidate" ? (
                    <>
                      <button
                        onClick={() =>
                          setAuthoringSignalId((cur) => (cur === s.id ? null : s.id))
                        }
                        disabled={decidingId !== null}
                        data-testid={`signal-action-promote-canonical-${s.id}`}
                        title="Accept as a canonical obligation — author + sign an official source (audit trail)"
                        style={decidingId === s.id ? actionInflightStyle : actionPromoteStyle}
                      >
                        {decidingId === s.id ? "deciding…" : "Promote → canonical"}
                      </button>
                      <button
                        onClick={() => handleDecide(s.id, "promote", "raglight")}
                        disabled={decidingId !== null}
                        data-testid={`signal-action-accept-advisory-${s.id}`}
                        title="Accept as advisory retrievable context (RAGLight) — never canonical"
                        style={decidingId === s.id ? actionInflightStyle : actionAdvisoryStyle}
                      >
                        Accept → advisory
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAcceptUseCase(s.id)}
                        disabled={decidingId !== null}
                        data-testid={`signal-action-accept-usecase-${s.id}`}
                        title="Add to U case intelligence — advisory public signal, never canonical or on the board"
                        style={decidingId === s.id ? actionInflightStyle : actionAdvisoryStyle}
                      >
                        Add → U case intel
                      </button>
                      <button
                        onClick={() => handleDecide(s.id, "dismiss")}
                        disabled={decidingId !== null}
                        data-testid={`signal-action-dismiss-${s.id}`}
                        title="Mark as not-canonical — logged in audit trail"
                        style={decidingId === s.id ? actionInflightStyle : actionDismissStyle}
                      >
                        Dismiss
                      </button>
                      {onReassignTopics && isVocSignal(s) && (
                        <button
                          type="button"
                          onClick={() => {
                            setReassigningSignalId((cur) => (cur === s.id ? null : s.id));
                            setReassignTopicIds([]);
                            setReassignFilter("");
                          }}
                          disabled={decidingId !== null}
                          data-testid={`signal-action-reassign-${s.id}`}
                          title="重新归类：替换该信号的 topic 归属（修正词表缺口/误选造成的错挂）"
                          style={actionAdvisoryStyle}
                        >
                          重新归类 ▾
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <span
                        data-testid={`signal-final-state-${s.id}`}
                        style={{
                          fontSize: 11,
                          color: "var(--color-text-secondary)",
                          fontStyle: "italic",
                        }}
                      >
                        {s.lifecycleState === "promoted"
                          ? "✓ Promoted — see audit trail"
                          : s.lifecycleState === "dismissed"
                            ? "✗ Dismissed — see audit trail"
                            : "Under review"}
                        {s.reviewerNotes && (
                          <>
                            {" · "}
                            <em>"{s.reviewerNotes}"</em>
                          </>
                        )}
                      </span>
                      {(s.lifecycleState === "promoted" || s.lifecycleState === "dismissed") && (
                        <button
                          onClick={() => handleDecide(s.id, "revert")}
                          disabled={decidingId !== null}
                          data-testid={`signal-action-revert-${s.id}`}
                          title="Undo this decision — restores the signal to candidate (audit trail)"
                          style={decidingId === s.id ? actionInflightStyle : actionDismissStyle}
                        >
                          {decidingId === s.id ? "deciding…" : "↶ Revert"}
                        </button>
                      )}
                    </>
                  )}
                </div>
                {authoringSignalId === s.id && s.lifecycleState === "candidate" && (
                  <RuleAuthoringForm
                    signal={s}
                    reviewer={reviewer}
                    submitting={decidingId === s.id}
                    onSubmit={(rule) => handleAuthorSubmit(s.id, rule)}
                    onCancel={() => setAuthoringSignalId(null)}
                  />
                )}
                {reassigningSignalId === s.id && (
                  <div
                    data-testid={`reassign-panel-${s.id}`}
                    style={{
                      marginTop: "var(--space-8)",
                      padding: "var(--space-8)",
                      border: "1px solid var(--color-border-strong)",
                      borderRadius: "var(--radius-4)",
                      display: "flex",
                      flexDirection: "column",
                      gap: "var(--space-4)",
                    }}
                  >
                    <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
                      选择新归属（将替换现有归属，可多选）
                      {reassignTopicIds.length > 0 ? ` · 已选 ${reassignTopicIds.length}` : ""}
                    </span>
                    <input
                      aria-label="筛选归类主题"
                      value={reassignFilter}
                      onChange={(e) => setReassignFilter(e.currentTarget.value)}
                      placeholder="筛选主题…"
                      style={{
                        fontSize: 12,
                        padding: "var(--space-4) var(--space-8)",
                        border: "1px solid var(--color-border-strong)",
                        borderRadius: "var(--radius-4)",
                      }}
                    />
                    <div
                      style={{
                        maxHeight: 120,
                        overflowY: "auto",
                        border: "1px solid var(--color-border)",
                        borderRadius: "var(--radius-4)",
                        padding: "var(--space-4)",
                      }}
                    >
                      {TOPIC_REGISTRY.filter((t) => t.id !== "unclassified")
                        .filter((t) => {
                          const q = reassignFilter.trim().toLowerCase();
                          if (!q) return true;
                          return (
                            t.id.toLowerCase().includes(q) ||
                            t.labelEn.toLowerCase().includes(q) ||
                            t.labelZh.toLowerCase().includes(q)
                          );
                        })
                        .map((t) => (
                          <label
                            key={t.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "var(--space-8)",
                              fontSize: 12,
                              padding: "2px 0",
                              cursor: "pointer",
                            }}
                          >
                            <input
                              type="checkbox"
                              data-testid={`reassign-opt-${s.id}-${t.id}`}
                              checked={reassignTopicIds.includes(t.id)}
                              onChange={(e) => {
                                const checked = e.currentTarget.checked;
                                setReassignTopicIds((prev) =>
                                  checked ? [...prev, t.id] : prev.filter((x) => x !== t.id),
                                );
                              }}
                            />
                            <span>
                              {t.labelZh} · {t.labelEn}
                            </span>
                          </label>
                        ))}
                    </div>
                    <div style={{ display: "flex", gap: "var(--space-8)" }}>
                      <button
                        type="button"
                        data-testid={`reassign-save-${s.id}`}
                        onClick={() => void handleReassignSave(s.id)}
                        disabled={reassignBusy || reassignTopicIds.length === 0}
                        style={{
                          fontSize: 11,
                          padding: "var(--space-4) var(--space-12)",
                          border: "1px solid var(--color-accent)",
                          background: "var(--color-accent-soft)",
                          color: "var(--color-accent)",
                          borderRadius: "var(--radius-4)",
                          cursor: reassignBusy ? "wait" : "pointer",
                          fontWeight: 600,
                        }}
                      >
                        {reassignBusy ? "保存中…" : "保存归类"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setReassigningSignalId(null)}
                        style={{
                          fontSize: 11,
                          padding: "var(--space-4) var(--space-12)",
                          border: "1px solid var(--color-border)",
                          background: "var(--color-surface)",
                          color: "var(--color-text-secondary)",
                          borderRadius: "var(--radius-4)",
                          cursor: "pointer",
                        }}
                      >
                        取消
                      </button>
                    </div>
                  </div>
                )}
                <div
                  style={{
                    marginTop: "var(--space-8)",
                    fontFamily: "var(--font-mono)",
                    fontSize: 10,
                    color: "var(--color-text-disabled)",
                    wordBreak: "break-all",
                  }}
                  title="provenance triplet"
                >
                  {sourceHost(s.sourceUrl)} · sha256={s.sha256.slice(0, 12)}… · chunk={s.chunkId}
                </div>
              </li>
            );
          })}
        </ul>
      </aside>
    </div>
  );
}

const actionPromoteStyle: React.CSSProperties = {
  padding: "var(--space-4) var(--space-8)",
  border: "1px solid var(--ft-hard-law)",
  background: "var(--ft-hard-law)",
  color: "var(--color-text-on-accent, white)",
  borderRadius: "var(--radius-4)",
  cursor: "pointer",
  fontSize: 11,
  fontWeight: 600,
};
const actionAdvisoryStyle: React.CSSProperties = {
  padding: "var(--space-4) var(--space-8)",
  border: "1px solid var(--color-accent)",
  background: "var(--color-accent-soft)",
  color: "var(--color-accent)",
  borderRadius: "var(--radius-4)",
  cursor: "pointer",
  fontSize: 11,
  fontWeight: 600,
};
const actionDismissStyle: React.CSSProperties = {
  padding: "var(--space-4) var(--space-8)",
  border: "1px solid var(--color-border-strong)",
  background: "var(--color-surface)",
  color: "var(--color-text-primary)",
  borderRadius: "var(--radius-4)",
  cursor: "pointer",
  fontSize: 11,
};
const actionInflightStyle: React.CSSProperties = {
  padding: "var(--space-4) var(--space-8)",
  border: "1px solid var(--color-border)",
  background: "var(--color-surface-sunken)",
  color: "var(--color-text-secondary)",
  borderRadius: "var(--radius-4)",
  cursor: "wait",
  fontSize: 11,
};
